'use client';

import { useEffect, useRef } from 'react';

import { trackEvent } from '@/lib/analytics';

/**
 * Reports which URL 404'd.
 *
 * The 404 page carried no instrumentation, so dead pages were invisible: GA4's
 * 30-day event list had no not-found event of any kind, and a `page_view` on the
 * 404 route looks identical to a successful one. That matters here specifically —
 * blog articles have shipped in the sitemap while returning 404 before, and the
 * only way that surfaced was someone noticing.
 *
 * `referrer` is the useful half: it says whether the dead link came from our own
 * sitemap, an internal page, or an external site, which decides whether the fix is
 * a redirect, a content repair, or nothing at all.
 */
export default function NotFoundTracker() {
  // React can run an effect twice in development; the ref keeps one 404 to one
  // event either way.
  const reported = useRef(false);

  useEffect(() => {
    if (reported.current) return;

    // The gtag stub is injected afterInteractive, and a mount effect can still
    // beat that script into the page. A one-shot send here lost the event
    // whenever it did; retry until the stub exists instead of giving up.
    const send = () => {
      if (typeof window.gtag !== 'function') return false;
      trackEvent('page_not_found', {
        page_path: `${window.location.pathname}${window.location.search}`,
        referrer: document.referrer || '(direct)',
      });
      return true;
    };

    if (send()) {
      reported.current = true;
      return;
    }

    const timer = window.setInterval(() => {
      if (send()) {
        reported.current = true;
        window.clearInterval(timer);
      }
    }, 250);
    return () => window.clearInterval(timer);
  }, []);

  return null;
}
